import {ChevronDown, Maximize2, Minimize2, PanelLeftClose, X} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";

import {ChatInterface} from "@components/ChatInterface";
import {Button} from "@components/ui/button";
import {
    ExpandableChat,
    ExpandableChatBody,
    ExpandableChatHeader,
    useChatMode,
} from "@components/ui/chat/expandable-chat";
import {useAgents} from "@contexts/AgentContext";
import {useChatContext} from "@contexts/ChatContext";

/**
 * Default tab → agent name mapping (legacy dashboard tabs only).
 * VNext tabs are handled via the isVNext flag from dashboardContext.
 */
const TAB_AGENT_MAP: Record<string, string> = {
    pnl: "P&L Forecast",
    forecast: "P&L Forecast",
    cashflow: "Cash Flow",
};

const DEFAULT_AGENT = "Virtual CFO";

function resolveAgent(
    agents: {slug: string; name: string; toolMode?: string}[],
    activeTab: string,
    selectedAgentSlug: string | null,
    isVNext: boolean,
) {
    const findByTarget = (target: string, pool: {slug: string; name: string; toolMode?: string}[]) =>
        pool.find((a) => a.slug === target)
        ?? pool.find((a) => a.name.toLowerCase().includes(target.toLowerCase()));

    const vnextAgents = agents.filter((a) => a.toolMode === "vnext");
    const effectiveVnextPool = vnextAgents.length > 0 ? vnextAgents : agents;

    // User-selected agent wins until changed manually.
    if (selectedAgentSlug) {
        const target = selectedAgentSlug;
        const matched = findByTarget(target, agents);
        if (matched) return matched;
    }

    // VNext context → pick a VNext-capable agent by tool mode first.
    if (isVNext) {
        const vnextAgent = findByTarget(DEFAULT_AGENT, effectiveVnextPool) ?? effectiveVnextPool[0];
        if (vnextAgent) return vnextAgent;
    }

    // Legacy tab mapping
    const target = TAB_AGENT_MAP[activeTab] ?? DEFAULT_AGENT;
    const matched = findByTarget(target, agents);
    return matched ?? agents[0] ?? null;
}

function FloatingChatInner({activeTab}: {activeTab: string}) {
    const {agents, loading} = useAgents();
    const {setChatDocked, setSidePanelOpen, selectedAgentSlug, setSelectedAgentSlug} = useChatContext();
    const {chatMode, setChatMode, dragOffset, setDragOffset} = useChatMode();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Track whether the current page is a VNext workspace
    const [isVNext, setIsVNext] = useState(false);
    useEffect(() => {
        const handler = (e: Event) => {
            setIsVNext(!!(e as CustomEvent).detail?.vnext);
        };
        window.addEventListener("dashboard-data-loaded", handler);
        return () => window.removeEventListener("dashboard-data-loaded", handler);
    }, []);

    // --- Drag handling ---
    const isDragging = useRef(false);
    const dragStart = useRef({x: 0, y: 0});
    const offsetStart = useRef({x: 0, y: 0});

    const onPointerDown = useCallback((e: React.PointerEvent) => {
        // Only left button
        if (e.button !== 0) return;
        isDragging.current = true;
        dragStart.current = {x: e.clientX, y: e.clientY};
        offsetStart.current = {x: dragOffset.x, y: dragOffset.y};
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        e.preventDefault();
    }, [dragOffset]);

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!isDragging.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setDragOffset({
            x: offsetStart.current.x + dx,
            y: offsetStart.current.y + dy,
        });
    }, [setDragOffset]);

    const onPointerUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    const currentAgent = resolveAgent(agents, activeTab, selectedAgentSlug, isVNext);
    const selectableAgents = isVNext
        ? (agents.filter((a) => a.toolMode === "vnext").length > 0
            ? agents.filter((a) => a.toolMode === "vnext")
            : agents)
        : agents;

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [dropdownOpen]);

    // Track which agent slugs have been visited so we only mount their
    // ChatInterface once (and keep it mounted for history preservation).
    const [visitedSlugs, setVisitedSlugs] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (currentAgent?.slug) {
            setVisitedSlugs((prev) => {
                if (prev.has(currentAgent.slug)) return prev;
                const next = new Set(prev);
                next.add(currentAgent.slug);
                return next;
            });
        }
    }, [currentAgent?.slug]);

    if (!currentAgent && !loading) return null;

    const agentName = currentAgent?.name || "Pecunio";

    return (
        <>
            <ExpandableChatHeader
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                style={{cursor: isDragging.current ? "grabbing" : "grab", touchAction: "none"}}
            >
                {/* Agent name + dropdown selector */}
                <div
                    ref={dropdownRef}
                    className="relative flex items-center gap-1"
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <button
                        className="flex items-center gap-1 font-semibold text-sm truncate hover:opacity-80"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        {agentName}
                        <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
                    </button>
                    {dropdownOpen && agents.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-zinc-900 border rounded-md shadow-lg py-1 min-w-[180px]">
                            {selectableAgents.map((a) => (
                                <button
                                    key={a.slug}
                                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                                        a.slug === currentAgent?.slug ? "font-semibold text-[#209DB3]" : ""
                                    }`}
                                    onClick={() => {
                                        setSelectedAgentSlug(a.slug);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    {a.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1" onPointerDown={(e) => e.stopPropagation()}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Dock to side panel"
                        onClick={() => {
                            setChatDocked(true);
                            setSidePanelOpen(true);
                        }}
                    >
                        <PanelLeftClose className="h-4 w-4"/>
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                            setChatMode(chatMode === "expanded" ? "popup" : "expanded")
                        }
                    >
                        {chatMode === "expanded" ? (
                            <Minimize2 className="h-4 w-4"/>
                        ) : (
                            <Maximize2 className="h-4 w-4"/>
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Close and dock to side panel"
                        onClick={() => {
                            setChatDocked(true);
                            setSidePanelOpen(true);
                        }}
                    >
                        <X className="h-4 w-4"/>
                    </Button>
                </div>
            </ExpandableChatHeader>
            <ExpandableChatBody>
                {agents
                    .filter((a) => visitedSlugs.has(a.slug))
                    .map((a) => (
                        <div
                            key={a.slug}
                            className={a.slug === currentAgent?.slug ? "h-full" : "hidden"}
                        >
                            <ChatInterface agent={a} loading={loading} containerMode="floating" />
                        </div>
                    ))}
                {!currentAgent && loading && (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                )}
            </ExpandableChatBody>
        </>
    );
}

export function FloatingChat() {
    const {activeTab, setActiveTab, chatDocked} = useChatContext();

    // Listen for tab changes from WorkspacePage (event bridge)
    useEffect(() => {
        const handler = (e: Event) => {
            const tab = (e as CustomEvent).detail?.tab;
            if (typeof tab === "string") setActiveTab(tab);
        };
        window.addEventListener("dashboard-tab-changed", handler);
        return () => window.removeEventListener("dashboard-tab-changed", handler);
    }, [setActiveTab]);

    // Don't render when chat is docked in side panel
    if (chatDocked) return null;

    return (
        <ExpandableChat position="bottom-right" size="lg" initialMode="popup">
            <FloatingChatInner activeTab={activeTab} />
        </ExpandableChat>
    );
}
